import { useEffect } from "react";

/** Tool names the AI agent can call that mutate a client's Files. */
export const AI_FILE_TOOLS = new Set([
  "rename_client_file",
  "move_client_file",
  "delete_client_file",
  "create_client_folder",
  "publish_private_file_to_shared",
  "rename_private_file",
  "delete_private_file",
]);

export const AI_FILE_CHANGED_EVENT = "ai-file-changed";

export interface FileChangedDetail {
  tool: string;
  id?: string;
  [key: string]: unknown;
}

/**
 * Notifies an open Client Files view when the AI agent renames, moves, or
 * deletes a file/folder — those changes bypass this page's own fetch calls,
 * so without this listener the UI only reflects them after a manual refresh.
 */
export function useFileChangeListener(onChanged: (detail: FileChangedDetail) => void) {
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<FileChangedDetail>).detail;
      if (detail) onChanged(detail);
    };
    window.addEventListener(AI_FILE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(AI_FILE_CHANGED_EVENT, handler);
  }, [onChanged]);
}

/**
 * The agent can never read/transfer files from the user's own device — the
 * most it can do is open the SAME native file picker the "Upload File" button
 * already opens, so the user picks files themselves. Kept separate from
 * AI_FILE_TOOLS/AI_FILE_CHANGED_EVENT above: this never mutates anything, so
 * it has nothing to "refresh" — it only opens a dialog.
 */
export const AI_FILE_UPLOAD_TOOLS = new Set(["open_file_upload"]);
export const AI_FILE_UPLOAD_REQUESTED_EVENT = "ai-file-upload-requested";

export function useFileUploadRequestListener(onRequested: () => void) {
  useEffect(() => {
    const handler = () => onRequested();
    window.addEventListener(AI_FILE_UPLOAD_REQUESTED_EVENT, handler);
    return () => window.removeEventListener(AI_FILE_UPLOAD_REQUESTED_EVENT, handler);
  }, [onRequested]);
}
